// =============================================================================
// SimSense Marketplace — Setup Detail Page (Route Wrapper)
// =============================================================================
// OT-279: Setup detail page with proof module, creator info, reviews.

import { useParams, useNavigate, Link } from "react-router-dom";
import { useSetupDetail } from "../../hooks/useMarketplace";
import { formatLapTime } from "../../utils/formatters";
import type { SetupReview } from "../../types/marketplace";
import "./SetupDetailPage.css";

function renderStars(rating: number) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return "★".repeat(full) + (half ? "★" : "") + "☆".repeat(empty);
}

function ReviewCard({ review }: { review: SetupReview }) {
  return (
    <div className="review-card">
      <div className="review-header">
        <span className="review-stars" title={`${review.rating}/5`}>
          {renderStars(review.rating)}
        </span>
        <span className="review-author">{review.userName}</span>
        {review.userIrating && (
          <span className="review-irating">{review.userIrating} iR</span>
        )}
      </div>
      {review.reviewText && <p className="review-text">{review.reviewText}</p>}
      <div className="review-meta">
        {review.lapsDriven && <span>{review.lapsDriven} laps driven</span>}
        {review.bestLapTime && (
          <span>Best: {formatLapTime(review.bestLapTime)}</span>
        )}
      </div>
    </div>
  );
}

export function SetupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { detail, reviews, loading } = useSetupDetail(id);

  if (loading) {
    return (
      <div className="detail-loading">Loading setup details...</div>
    );
  }

  if (!detail) {
    return (
      <div className="detail-not-found">
        <h2>Setup not found</h2>
        <button onClick={() => navigate("/marketplace")}>
          Back to Marketplace
        </button>
      </div>
    );
  }

  const handleInstall = () => {
    // TODO: Wire to Tauri desktop install or Stripe purchase flow
    console.log(`Installing setup ${detail.id}`);
  };

  const handleCompare = () => {
    if (detail.telemetrySessionId) {
      navigate(`/session/${detail.telemetrySessionId}/compare`);
    }
  };

  return (
    <div className="setup-detail-page">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <Link to="/marketplace">Marketplace</Link>
        <span className="bc-sep">/</span>
        <span>{detail.carName}</span>
        <span className="bc-sep">/</span>
        <span className="bc-active">{detail.setupName}</span>
      </div>

      <div className="detail-grid">
        {/* Left Column */}
        <div className="detail-main">
          <header className="detail-header">
            <h1>{detail.setupName}</h1>
            <div className="creator-byline">
              By{" "}
              {detail.creatorSlug ? (
                <Link
                  to={`/marketplace/shop/${detail.creatorSlug}`}
                  className="creator-link"
                >
                  {detail.creatorName}
                </Link>
              ) : (
                <span>{detail.creatorName}</span>
              )}
              {detail.isVerified && (
                <span className="verified-badge-lg">
                  {detail.lane === "shop" ? "Verified Shop" : detail.lane === "pro_builder" ? "Verified Creator" : "Community"}
                </span>
              )}
              {detail.skillLevel && (
                <span className={`skill-badge skill-${detail.skillLevel}`}>
                  {detail.skillLevel}
                </span>
              )}
            </div>
          </header>

          {/* The Proof Module */}
          <section className="proof-module">
            <div className="proof-module-header">
              <h2>The Proof</h2>
              <button className="btn-secondary" onClick={handleCompare}>
                Compare with my best
              </button>
            </div>
            <div className="proof-stats">
              <div className="stat-box stat-primary">
                <span className="stat-label">PROVEN LAP TIME</span>
                <span className="stat-value">
                  {formatLapTime(detail.bestLapTime)}
                </span>
              </div>
              {detail.conditions && (
                <>
                  <div className="stat-box">
                    <span className="stat-label">AIR TEMP</span>
                    <span className="stat-value">
                      {detail.conditions.airTemp}°F
                    </span>
                  </div>
                  <div className="stat-box">
                    <span className="stat-label">TRACK TEMP</span>
                    <span className="stat-value">
                      {detail.conditions.trackTemp}°F
                    </span>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Creator Notes */}
          <section className="setup-notes">
            <h2>Creator Notes</h2>
            <p>{detail.description}</p>
            {detail.tags.length > 0 && (
              <div className="tags-container">
                {detail.tags.map((tag) => (
                  <span key={tag} className="tag-pill">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Reviews */}
          {reviews.length > 0 && (
            <section className="reviews-section">
              <h2>Reviews ({reviews.length})</h2>
              <div className="reviews-list">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right Column — Sticky Purchase Box */}
        <div className="detail-sidebar">
          <div className="purchase-card">
            <div className="purchase-price">
              {detail.isFree
                ? "FREE"
                : `$${(detail.priceCents / 100).toFixed(2)}`}
            </div>

            <button className="btn-install-large" onClick={handleInstall}>
              {detail.isFree ? "Install to iRacing" : "Buy & Install"}
            </button>

            <div className="meta-list">
              <div className="meta-item">
                <span className="meta-label">Car</span>
                <span className="meta-value">{detail.carName}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Track</span>
                <span className="meta-value">{detail.trackName}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Type</span>
                <span className="meta-value">{detail.type}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Season</span>
                <span className="meta-value">{detail.season}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Downloads</span>
                <span className="meta-value">
                  {detail.downloadCount.toLocaleString()}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Last Updated</span>
                <span className="meta-value">{detail.lastUpdated}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
